import { Injectable } from '@angular/core';
import { CoreGoogleAnalyticActions } from '@fantaz/core/analytic';
import { AuthActions, AuthState } from '@fantaz/shared/auth';
import { ProfileApiService } from '@fantaz/shared/data-access/api';
import {
  DuplicateUsernameFailure,
  GAEventKeys,
  UsernameBlackListFailure,
} from '@fantaz/shared/data-access/models';
import { UIStore } from '@fantaz/shared/data-access/store';
import { SnackbarService } from '@fantaz/shared/snackbar';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { of } from 'rxjs';
import {
  catchError,
  exhaustMap,
  map,
  mergeMap,
  switchMap,
  tap,
} from 'rxjs/operators';
import * as ProfileActions from './profile.actions';

@Injectable()
export class ProfileEffects {
  init$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        AuthActions.AuthCurrentUserActions.loadCurrentUserSuccess,
        ProfileActions.updateUserContactAttributeSuccess,
      ),
      mergeMap(() => {
        return [ProfileActions.loadProfile()];
      }),
    ),
  );

  loadUserProfile$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProfileActions.loadProfile),
      switchMap(() => {
        return this.profileApiService.fetchUserProfile().pipe(
          map((profile) => {
            // set stored timezone
            this.uiStore.setTimezone(profile?.userSettings?.timezone);

            return ProfileActions.loadProfileSuccess({
              profile,
            });
          }),
          catchError((error) => {
            console.error('Error', error);
            return of(ProfileActions.loadProfileFailure({ error }));
          }),
        );
      }),
    ),
  );

  /**
   * Effect to update the user profile
   */
  update$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProfileActions.update),
      mergeMap((action) => {
        return this.profileApiService
          .updateUserProfile({
            ...action.payload,
          })
          .pipe(
            mergeMap((res) => {
              if (res.error) {
                return [
                  ProfileActions.updateProfileFailure({
                    error: res.error?.message,
                  }),
                ];
              } else {
                if (!(action.options?.silent == true)) {
                  this.toastService.openSnackBar(
                    action.options?.msg || 'Profile updated!',
                  );
                }

                // update app timezone
                this.uiStore.setTimezone(
                  action.payload?.userSettings?.timezone,
                );

                return [
                  ProfileActions.updateProfileSuccess({
                    response: res,
                    profile: action.payload,
                  }),
                  AuthActions.updateUserLocally({
                    user: {
                      firstName: res?.payload?.firstName,
                      lastName: res?.payload?.lastName,
                      username: res?.payload?.username,
                      shippingAddress: res?.payload?.shippingAddress,
                    },
                  }),
                ];
              }
            }),
            catchError<any, any>((error: any) => {
              let message = '';
              if (error instanceof DuplicateUsernameFailure) {
                message = `The profile name is already in use. Please choose a different name.`;
              } else if (error instanceof UsernameBlackListFailure) {
                message = `Oops, that profile name isn’t allowed. Please pick a different one and try again.`;
              } else {
                message = 'Profile update failed';
              }
              if (!(action.options?.silent == true) && message != '') {
                this.toastService.openSnackBar(message);
              }
              return of(ProfileActions.updateProfileFailure({ error }));
            }),
          );
      }),
    ),
  );
  /**
   * Effect to update the user profile
   */
  updateUserContactAttribute$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProfileActions.updateUserContactAttribute),
      switchMap((action) => {
        return this.profileApiService
          .updateUserContactAttribute({
            ...action.payload,
          })
          .pipe(
            map((res) => {
              return ProfileActions.updateUserContactAttributeSuccess(res);
            }),
            catchError<any, any>((error: any) => {
              return ProfileActions.updateUserContactAttributeFailure({
                error,
              });
            }),
          );
      }),
      catchError((error) => {
        return of(ProfileActions.updateUserContactAttributeFailure({ error }));
      }),
    ),
  );

  /**
   * Effect to loadProfileSuccess the user profile
   */
  loadProfileSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProfileActions.updateProfileSuccess),
      exhaustMap(() => {
        return [
          CoreGoogleAnalyticActions.trackEvent({
            event: {
              event: GAEventKeys.USER_PROFILE,
              verification_action: 'save-personal-info-success',
              verification_status: `save-personal-info-success`,
            },
          }),
        ];
      }),
    ),
  );

  savePrefsEffect$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(ProfileActions.updateProfileSuccess),
        tap(({ profile }) => {
          localStorage.setItem('prefs', JSON.stringify(profile?.userSettings));
        }),
      ),
    {
      dispatch: false,
    },
  );

  constructor(
    private readonly actions$: Actions,
    private readonly authStore: Store<AuthState>,
    private readonly profileApiService: ProfileApiService,
    private readonly toastService: SnackbarService,
    private readonly uiStore: UIStore,
  ) {}
}
